import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useClickOutside } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import {
  useDebounce,
  useDeviceLayout,
  useAddressSearch,
  useSearchKeyboard,
  useUserLocation,
} from '../../../hooks';
import { useGeoMarkupStore } from '../../../store';
import {
  selectSelectedPlaceTypes,
  selectUseLocationPriority,
  selectRecentSearches,
} from '../../../store/selectors/searchSelectors';
import type { SearchHistoryItem } from '../../../utils/searchHistory';
import {
  SearchInput,
  SearchResults,
  PlaceTypeFilterButton,
  PlaceTypeFilterContent,
} from './components';
import type { AddressSearchBarProps, AddressResult } from './types';
import styles from './AddressSearchBar.module.css';

/** Icon size for collapsed button */
const COLLAPSE_BUTTON_ICON_SIZE = 20;

/** Duration of collapse animation in milliseconds */
const COLLAPSE_ANIMATION_DURATION_MS = 250;

const AddressSearchBarComponent: React.FC<AddressSearchBarProps> = ({
  onSelectLocation,
  placeholder,
  className,
  disabled = false,
  collapsible = false,
  defaultCollapsed = true,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useDeviceLayout();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Collapsible mode state - collapsed by default when collapsible is enabled
  const [isCollapsed, setIsCollapsed] = useState(
    collapsible && defaultCollapsed
  );
  const [isCollapsing, setIsCollapsing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Custom hooks
  const {
    results,
    isLoading,
    error,
    searchAddresses,
    clearResults,
    clearError,
  } = useAddressSearch();
  const {
    location: userLocation,
    hasPermission,
    getCurrentLocation,
  } = useUserLocation();

  // Zustand selectors (atomic - minimal re-renders)
  const selectedPlaceTypes = useGeoMarkupStore(selectSelectedPlaceTypes);
  const useLocationPriority = useGeoMarkupStore(selectUseLocationPriority);
  const recentSearches = useGeoMarkupStore(selectRecentSearches);
  const setPlaceTypes = useGeoMarkupStore((s) => s.setPlaceTypes);
  const setLocationPriority = useGeoMarkupStore((s) => s.setLocationPriority);
  const addRecentSearch = useGeoMarkupStore((s) => s.addRecentSearch);

  // Trigger collapse animation before actually collapsing
  const triggerCollapse = useCallback(() => {
    if (!isCollapsing && !isCollapsed) {
      setIsCollapsing(true);
      setTimeout(() => {
        setIsCollapsed(true);
        setIsCollapsing(false);
      }, COLLAPSE_ANIMATION_DURATION_MS);
    }
  }, [isCollapsing, isCollapsed]);

  // Close dropdown when clicking outside (and collapse if query is empty)
  const containerRef = useClickOutside(() => {
    // Suppress map interactions when dropdown/filters are open OR search bar is expanded
    // This prevents blocking right-click context menu when everything is closed/collapsed
    if (isOpen || showFilters || (collapsible && !isCollapsed)) {
      useGeoMarkupStore.getState().ui.suppressInteraction();
    }
    setIsOpen(false);
    setShowFilters(false);
    // Collapse when clicking outside if collapsible mode and query is empty
    if (collapsible && !query.trim()) {
      triggerCollapse();
    }
  }, ['mousedown', 'touchstart']);

  // Debounce search query with optimized 200ms delay
  const { debouncedValue: debouncedQuery, cancel: cancelSearch } = useDebounce(
    query,
    200,
    {
      trailing: true,
      maxWait: 1000, // Force execution after 1000ms max for better UX
    }
  );

  // Effects for search and results
  useEffect(() => {
    if (debouncedQuery?.trim().length >= 3) {
      setShowRecentSearches(false);
      const userLocationToUse =
        useLocationPriority && userLocation ? userLocation : undefined;
      searchAddresses(debouncedQuery, selectedPlaceTypes, userLocationToUse);
    } else if (!debouncedQuery?.trim()) {
      clearResults();
      // Prepare recent searches if input is empty, but don't auto-open dropdown
      if (recentSearches.length > 0) {
        setShowRecentSearches(true);
        // Don't auto-open - wait for user focus
      } else {
        setShowRecentSearches(false);
        // Don't close dropdown if filters panel is open
        if (!showFilters) {
          setIsOpen(false);
        }
      }
    }
  }, [
    debouncedQuery,
    searchAddresses,
    clearResults,
    recentSearches.length,
    selectedPlaceTypes,
    useLocationPriority,
    userLocation,
    showFilters,
  ]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      cancelSearch();
    };
  }, [cancelSearch]);

  // Auto-fetch location when permission is granted and priority is enabled
  useEffect(() => {
    if (useLocationPriority && hasPermission && !userLocation) {
      getCurrentLocation().catch(() => {
        // Silently fail - feature will remain disabled until user grants permission
      });
    }
  }, [useLocationPriority, hasPermission, userLocation, getCurrentLocation]);

  useEffect(() => {
    if (results.length > 0) {
      setIsOpen(true);
      setSelectedIndex(-1);
    }
  }, [results]);

  // Memoized event handlers
  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (!value.trim()) {
        // Prepare recent searches when input becomes empty, but don't auto-open
        if (recentSearches.length > 0) {
          setShowRecentSearches(true);
          // Don't auto-open - only open on focus
        } else {
          setShowRecentSearches(false);
          setIsOpen(false);
        }
      } else {
        setShowRecentSearches(false);
        setShowFilters(false); // Close filters when user types
      }
    },
    [recentSearches.length]
  );

  // Collapse back to the icon when focus leaves the bar (collapsible mode,
  // empty query, filters closed) — mirrors the main map's "hide on blur"
  // behaviour reliably even where document-level click-outside doesn't fire
  // (e.g. the always-open SlidePanel + map canvas on the configurator page).
  // The 250ms collapse animation leaves time for a result/recent-search tap.
  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!collapsible || query.trim() || showFilters) return;
      const nextTarget = event.relatedTarget as Node | null;
      if (nextTarget && containerRef.current?.contains(nextTarget)) return;
      // Mirror the useClickOutside path: the tap that blurs the bar (landing on
      // the map canvas) must be suppressed so it doesn't open the add-artifact
      // menu — on mobile the collapse runs through blur, not click-outside.
      useGeoMarkupStore.getState().ui.suppressInteraction();
      triggerCollapse();
    },
    [collapsible, query, showFilters, containerRef, triggerCollapse]
  );

  const handleInputFocus = useCallback(() => {
    setShowFilters(false); // Collapse filters when input receives focus
    if (!query.trim() && recentSearches.length > 0) {
      setShowRecentSearches(true);
      setIsOpen(true);
    } else if (results.length > 0) {
      setIsOpen(true);
    }
  }, [query, recentSearches.length, results.length]);

  const handleClearSearch = useCallback(() => {
    setQuery('');
    clearResults();
    clearError();
    setShowFilters(false);
    // Show recent searches after clearing if there are any
    if (recentSearches.length > 0) {
      setShowRecentSearches(true);
      setIsOpen(true);
    }
    if (isDesktop) {
      inputRef.current?.focus();
    }
  }, [isDesktop, clearResults, clearError, recentSearches.length]);

  const handleToggleFilters = useCallback(() => {
    setShowFilters(!showFilters);
    setIsOpen(true); // Always open dropdown when toggling filters
  }, [showFilters]);

  const handleSelection = useCallback(
    (lat: number, lng: number, displayName: string, type?: string) => {
      addRecentSearch({
        id: `${lat}-${lng}-${Date.now()}`,
        display_name: displayName,
        coordinates: { lat, lng },
        timestamp: Date.now(),
        type,
      });
      setQuery(displayName);
      setIsOpen(false);
      setShowFilters(false);
      clearResults();
      setShowRecentSearches(false);
      onSelectLocation?.(lat, lng, displayName);
    },
    [addRecentSearch, clearResults, onSelectLocation]
  );

  const handleSelectLocation = useCallback(
    (result: AddressResult) => {
      handleSelection(
        parseFloat(result.lat),
        parseFloat(result.lon),
        result.display_name,
        result.type
      );
    },
    [handleSelection]
  );

  const handleSelectRecentSearch = useCallback(
    (recentSearch: SearchHistoryItem) => {
      const { lat, lng } = recentSearch.coordinates;
      handleSelection(lat, lng, recentSearch.display_name, recentSearch.type);
    },
    [handleSelection]
  );

  // Keyboard navigation hook
  const { handleKeyDown } = useSearchKeyboard({
    isOpen,
    results,
    recentSearches,
    showRecentSearches,
    selectedIndex,
    setSelectedIndex,
    setIsOpen,
    setShowRecentSearches,
    onSelectResult: handleSelectLocation,
    onSelectRecentSearch: handleSelectRecentSearch,
    inputRef,
  });

  // Memoized values
  const hasResultsOrRecent = useMemo(
    () => results.length > 0 || showRecentSearches || !!error || showFilters,
    [results.length, showRecentSearches, error, showFilters]
  );

  // Handle expand click (collapsible mode)
  const handleExpandClick = useCallback(() => {
    setIsCollapsed(false);
    // Focus input after expansion animation
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);

  // Render collapsed state (only search button) - but not during collapse animation
  if (collapsible && isCollapsed && !isCollapsing) {
    return (
      <div className={`${styles.collapsedContainer} ${className || ''}`}>
        <button
          className={`${styles.collapseButton} semi-transparent-panel`}
          onClick={handleExpandClick}
          aria-label={t('tooltips.map.showSearch')}
          type="button"
          disabled={disabled}
        >
          <Search size={COLLAPSE_BUTTON_ICON_SIZE} />
        </button>
      </div>
    );
  }

  // Render expanded state (full search interface)
  const containerClasses = [
    styles.container,
    isOpen ? styles.containerOpen : '',
    collapsible && !isCollapsing ? styles.collapsibleExpanded : '',
    isCollapsing ? styles.collapsibleCollapsing : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <div ref={containerRef} className={containerClasses}>
        <div className={`${styles.searchContainer} semi-transparent-panel`}>
          <div className={styles.searchHeader}>
            <div className={styles.inputWrapper}>
              <SearchInput
                query={query}
                onQueryChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onClear={handleClearSearch}
                isLoading={isLoading}
                disabled={disabled}
                placeholder={placeholder || t('search.address.placeholder')}
                inputRef={inputRef}
              />
            </div>
            <div className={styles.filterWrapper}>
              <PlaceTypeFilterButton
                selectedTypes={selectedPlaceTypes}
                onTypesChange={setPlaceTypes}
                useLocationPriority={useLocationPriority}
                onLocationPriorityChange={setLocationPriority}
                hasUserLocation={!!userLocation}
                onToggle={handleToggleFilters}
                isOpen={showFilters}
              />
            </div>
          </div>
          {isOpen && hasResultsOrRecent && (
            <>
              <div className={styles.separator} />
              <div className={styles.resultsContainer}>
                {showFilters && (
                  <PlaceTypeFilterContent
                    selectedTypes={selectedPlaceTypes}
                    onTypesChange={setPlaceTypes}
                    useLocationPriority={useLocationPriority}
                    onLocationPriorityChange={setLocationPriority}
                    hasUserLocation={!!userLocation}
                  />
                )}
                {!showFilters && (
                  <SearchResults
                    results={results}
                    selectedIndex={selectedIndex}
                    onSelectResult={handleSelectLocation}
                    showRecentSearches={showRecentSearches}
                    recentSearches={recentSearches}
                    onSelectRecentSearch={handleSelectRecentSearch}
                    error={error}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders when props don't change
export const AddressSearchBar = React.memo(AddressSearchBarComponent);
